import { getApiBaseUrl, SKIN_API_PREFIX } from './apiConfig';
import { normalizeSkinReportPayload } from './e2eMedia';
import { fetchJsonWithRetry } from './fetchWithRetry';
import { logSkinResilience, TIMEOUT_MS } from './skinResilience';
import { recordSkinTelemetry } from './skinTelemetry';

/**
 * Signed share tokens: "{expires_unix}.{hmac_signature}" (see report_access_token.py).
 */
export function isSignedReportAccessToken(token) {
  const raw = String(token || '').trim();
  if (!raw || !raw.includes('.')) return false;
  const parts = raw.split('.');
  if (parts.length !== 2) return false;
  return /^\d+$/.test(parts[0]) && parts[1].length > 0;
}

/** Brand-admin app_session_token JWT — must not be sent to GET /api/skin/report. */
export function isAppSessionJwt(token) {
  const raw = String(token || '').trim();
  const parts = raw.split('.');
  return parts.length === 3 && parts[0].startsWith('eyJ');
}

function decodeJwtPayload(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return null;
    const padded = part + '='.repeat((4 - (part.length % 4)) % 4);
    return JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

function classifyReportQueryToken(token) {
  const raw = String(token || '').trim();
  if (!raw) return 'none';
  if (isSignedReportAccessToken(raw)) return 'signed_share';
  if (isAppSessionJwt(raw)) return 'app_session_jwt';
  return 'unknown';
}

function logReportTokenDebug(skinReference, rawToken, reportToken) {
  const claims = isAppSessionJwt(rawToken) ? decodeJwtPayload(rawToken) : null;
  logSkinResilience('report_token_debug', {
    skinReference,
    queryTokenKind: classifyReportQueryToken(rawToken),
    sendsReportToken: Boolean(reportToken),
    jwtExp: claims?.exp ?? null,
    jwtUserId: claims?.user_id ?? claims?.userId ?? null,
    serverTime: Math.floor(Date.now() / 1000),
  });
}

/**
 * @param {string} skinReference
 * @param {{ accessToken?: string, signal?: AbortSignal, onRetry?: Function }} [options]
 */
export async function fetchSkinReport(skinReference, options = {}) {
  const ref = String(skinReference || '').trim();
  if (!ref) throw new Error('skinreference is required');

  const base = getApiBaseUrl();
  const rawToken = String(options.accessToken || '').trim();
  const reportToken = isSignedReportAccessToken(rawToken) ? rawToken : '';
  logReportTokenDebug(ref, rawToken, reportToken);

  const requestReport = async (tokenValue, attemptLabel = 'primary') => {
    logSkinResilience('report_fetch', {
      skinReference: ref,
      attempt: attemptLabel,
      withToken: Boolean(tokenValue),
      tokenKind: classifyReportQueryToken(tokenValue),
    });
    let requestUrl = `${base}${SKIN_API_PREFIX}/skin/report?skinreference=${encodeURIComponent(ref)}`;
    if (tokenValue) {
      requestUrl = `${requestUrl}&token=${encodeURIComponent(tokenValue)}`;
    }
    const data = await fetchJsonWithRetry(
      requestUrl,
      { method: 'GET', timeoutMs: TIMEOUT_MS.REPORT, signal: options.signal },
      { label: 'skin-report', onRetry: options.onRetry },
    );
    if (!data.success) {
      const err = new Error(data.error || 'Failed to load report');
      err.status = data.code === 'expired_token' ? 403 : undefined;
      err.apiResponse = data;
      throw err;
    }
    return normalizeSkinReportPayload(data);
  };

  try {
    try {
      return await requestReport(reportToken, 'primary');
    } catch (firstErr) {
      const authFailed = firstErr.status === 401 || firstErr.status === 403
        || firstErr.apiResponse?.code === 'expired_token'
        || firstErr.apiResponse?.code === 'invalid_token';
      if (authFailed && (reportToken || rawToken)) {
        logSkinResilience('report_fetch_retry', {
          skinReference: ref,
          reason: firstErr.apiResponse?.code || firstErr.status,
          droppedQueryToken: true,
        });
        return await requestReport('', 'without_token');
      }
      throw firstErr;
    }
  } catch (err) {
    recordSkinTelemetry('report_load_failure', {
      skinReference: ref,
      reason: err.message,
      status: err.status,
    });
    throw err;
  }
}

/**
 * Mint a signed share URL from the backend (requires skin_reference ownership when token is set).
 * @param {{ skinReference: string, appSessionToken?: string, ttlSeconds?: number, baseUrl?: string }} params
 */
export async function createSkinReportShareUrl({
  skinReference,
  appSessionToken,
  ttlSeconds,
  baseUrl,
}) {
  const ref = String(skinReference || '').trim();
  if (!ref) throw new Error('skin_reference is required');

  const base = getApiBaseUrl();
  const body = { skin_reference: ref };
  if (appSessionToken) body.app_session_token = appSessionToken;
  if (ttlSeconds != null) body.ttl_seconds = ttlSeconds;
  if (baseUrl) body.base_url = baseUrl;

  const data = await fetchJsonWithRetry(
    `${base}${SKIN_API_PREFIX}/skin/report/share-url`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      timeoutMs: TIMEOUT_MS.REPORT,
    },
    { label: 'skin-report-share-url', maxAttempts: 2 }
  );
  if (!data.success) {
    throw new Error(data.error || 'Failed to create share URL');
  }
  return data;
}

/**
 * Fetch decrypted captured views (data URLs) — same path as detect flow.
 * @param {string} skinReference
 * @param {{ appSessionToken?: string, signal?: AbortSignal }} [options]
 */
export async function fetchCapturedViews(skinReference, options = {}) {
  const ref = String(skinReference || '').trim();
  if (!ref) throw new Error('skinreference is required');

  const base = getApiBaseUrl();
  const appSessionToken = String(options.appSessionToken || '').trim();
  let url = `${base}${SKIN_API_PREFIX}/captured-views?session_id=${encodeURIComponent(ref)}`;
  if (appSessionToken) {
    url = `${url}&app_session_token=${encodeURIComponent(appSessionToken)}`;
  }

  const data = await fetchJsonWithRetry(
    url,
    { method: 'GET', timeoutMs: TIMEOUT_MS.REPORT, signal: options.signal },
    { label: 'captured-views', maxAttempts: 2 },
  );
  if (!data.success) {
    throw new Error(data.error || 'Failed to load captured views');
  }
  return data;
}

/** Short share path — omit app-session JWT; include only signed report access tokens. */
export function buildSkinResultPagePath(skinReference, accessToken) {
  const ref = encodeURIComponent(String(skinReference || '').trim());
  const path = `/skin-result?skinreference=${ref}`;
  const token = String(accessToken || '').trim();
  if (!isSignedReportAccessToken(token)) return path;
  return `${path}&token=${encodeURIComponent(token)}`;
}
